import type { FC } from 'react';

import { intervalToDuration } from 'date-fns';

import cx from 'classnames';

import type { IPlayer } from '../../types';

import styles from './PlayerCard.module.scss';

type TPlayerCardSize = 'default' | 'desktopBlock' | 'medium' | 'small';

type TPlayerCardDisplayMode = 'default' | 'indicatorNameTimer';
type TStatusIndicatorVariant = 'active' | 'primary' | 'accent';

interface IPlayerCardProps {
  player: IPlayer;
  isActive: boolean;
  timeLeftMs: number;
  size?: TPlayerCardSize;
  displayMode?: TPlayerCardDisplayMode;
  statusIndicatorVariant?: TStatusIndicatorVariant;
}

const formatTime = (ms: number): string => {
  const d = intervalToDuration({ start: 0, end: Math.max(0, ms) });
  const totalMinutes = (d.hours ?? 0) * 60 + (d.minutes ?? 0);
  const seconds = String(d.seconds ?? 0).padStart(2, '0');
  return `${totalMinutes}:${seconds}`;
};

const TimerIcon: FC = () => (
  <svg
    className={styles.timerIcon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g clipPath="url(#timerIconClip)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.019 1.74209e-07H12.1529C14.7176 -1.15331e-05 16.7321 -2.31848e-05 18.3049 0.211434C19.9165 0.428113 21.1953 0.881233 22.2 1.88596C23.2047 2.89068 23.6578 4.16941 23.8745 5.78111C24.0859 7.35389 24.0859 9.36835 24.0859 11.9331V12.0669C24.0859 14.6316 24.0859 16.6461 23.8745 18.2189C23.6578 19.8306 23.2047 21.1093 22.2 22.114C21.1953 23.1187 19.9165 23.5719 18.3049 23.7886C16.7321 24.0001 14.7176 24.0001 12.1529 24H12.019C9.45427 24.0001 7.43982 24.0001 5.86703 23.7886C4.25535 23.5719 2.9766 23.1187 1.97188 22.114C0.967171 21.1093 0.514051 19.8306 0.29736 18.2189C0.0859143 16.6461 0.085926 14.6316 0.0859377 12.0669V11.9331C0.085926 9.36834 0.0859143 7.35388 0.29736 5.78111C0.514051 4.16941 0.967171 2.89068 1.97188 1.88596C2.9766 0.881233 4.25535 0.428113 5.86703 0.211434C7.43982 -2.31848e-05 9.45427 -1.15331e-05 12.019 1.74209e-07ZM6.10104 1.95187C4.68407 2.14238 3.83769 2.50365 3.21363 3.1277C2.58958 3.75175 2.22831 4.59813 2.0378 6.0151C1.8439 7.45733 1.84204 9.35371 1.84204 12C1.84204 14.6463 1.8439 16.5426 2.0378 17.9849C2.22831 19.4019 2.58958 20.2482 3.21363 20.8723C3.83769 21.4963 4.68407 21.8576 6.10104 22.0481C7.54325 22.2421 9.43965 22.2439 12.0859 22.2439C14.7323 22.2439 16.6286 22.2421 18.0708 22.0481C19.4878 21.8576 20.3342 21.4963 20.9582 20.8723C21.5823 20.2482 21.9436 19.4019 22.1341 17.9849C22.328 16.5426 22.3298 14.6463 22.3298 12C22.3298 9.35371 22.328 7.45733 22.1341 6.0151C21.9436 4.59813 21.5823 3.75175 20.9582 3.1277C20.3342 2.50365 19.4878 2.14238 18.0708 1.95187C16.6286 1.75797 14.7323 1.7561 12.0859 1.7561C9.43965 1.7561 7.54325 1.75797 6.10104 1.95187Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.0834 5.26953C12.5683 5.26953 12.9614 5.66265 12.9614 6.14758V9.51698C13.9845 9.87858 14.7175 10.8543 14.7175 12.0012C14.7175 12.408 14.6254 12.793 14.4608 13.137L16.2164 14.8926C16.5593 15.2355 16.5593 15.7914 16.2164 16.1343C15.8735 16.4772 15.3176 16.4772 14.9747 16.1343L13.2191 14.3786C12.8751 14.5432 12.4901 14.6354 12.0834 14.6354C10.6286 14.6354 9.44922 13.456 9.44922 12.0012C9.44922 10.8543 10.1822 9.87858 11.2053 9.51698V6.14758C11.2053 5.66265 11.5984 5.26953 12.0834 5.26953ZM12.0834 11.1232C11.5984 11.1232 11.2053 11.5163 11.2053 12.0012C11.2053 12.4862 11.5984 12.8793 12.0834 12.8793C12.5683 12.8793 12.9614 12.4862 12.9614 12.0012C12.9614 11.5163 12.5683 11.1232 12.0834 11.1232Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="timerIconClip">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const PlayerCard: FC<IPlayerCardProps> = ({
  displayMode = 'default',
  isActive,
  player,
  size = 'default',
  statusIndicatorVariant = 'active',
  timeLeftMs,
}) => {
  const symbolClass =
    player.symbolContent == null && player.symbol === 'X'
      ? styles.symbolX
      : player.symbolContent == null && player.symbol === 'O'
        ? styles.symbolO
        : '';
  const sizeClass = size !== 'default' ? styles[size] : '';
  const statusIndicatorVariantClass =
    statusIndicatorVariant === 'primary'
      ? styles.statusIndicatorPrimary
      : statusIndicatorVariant === 'accent'
        ? styles.statusIndicatorAccent
        : '';

  if (displayMode === 'indicatorNameTimer') {
    return (
      <div
        className={cx(
          styles.playerCard,
          styles.mobileIndicatorStack,
          isActive && styles.active,
          statusIndicatorVariantClass,
          sizeClass,
        )}
      >
        <div className={styles.statusIndicator} aria-hidden />
        <div className={styles.playerName}>{player.name}</div>
        <div className={styles.timer}>
          <TimerIcon />
          <span>{formatTime(timeLeftMs)}</span>
        </div>
      </div>
    );
  }

  if (size === 'desktopBlock') {
    return (
      <div
        className={cx(
          styles.playerCard,
          styles.desktopBlock,
          isActive && styles.active,
          statusIndicatorVariantClass,
        )}
      >
        <div className={styles.statusIndicator} aria-hidden />
        <span className={styles.playerName}>{player.name}</span>
        <div className={styles.timer}>
          <TimerIcon />
          <span>{formatTime(timeLeftMs)}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(styles.playerCard, isActive && styles.active, sizeClass)}
    >
      <div className={styles.avatarWrapper}>
        {player.avatarUrl ? (
          <img
            alt={player.name}
            className={styles.avatar}
            src={player.avatarUrl}
          />
        ) : (
          <div className={styles.avatarPlaceholder}>
            {player.name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      <div className={styles.playerName}>{player.name}</div>

      <div className={cx(styles.playerSymbol, symbolClass)}>
        {player.symbolContent != null ? player.symbolContent : player.symbol}
      </div>

      <div className={styles.timer}>
        <TimerIcon />
        <span>{formatTime(timeLeftMs)}</span>
      </div>
    </div>
  );
};
